// pipe: 4B.8 (Task 12) — the PURE PickupCompleteBody construction extracted from
// the pickup-execution container (DRY): the container's `onComplete` and the
// Vitest seam both build the body through this one helper, so the load-bearing
// client invariants are tested at the seam instead of buried in the container.
//
// This file imports ONLY type-only service types (erased by swc) + the view's
// `GpsState` type — NO runtime DriverErpService, react-router, or DI — so the
// pure-view spec can import it without pulling the container's runtime plumbing.
//
// THE LOAD-BEARING LOGIC (the brief):
//  - `geo` is set ONLY when coords were obtained (omitted on 'pending'/'denied' —
//    GPS is exceeds-standard, so a denial does NOT block completion);
//  - `net_weight_kg` is set ONLY when a number was entered;
//  - `net_weight_override_reason` is set ONLY when the entered net weight deviates
//    from the declared weight AND a non-empty reason is present.
import type { PickupCompleteBody } from '../services/driver-erp';
import type { GpsState } from './pickup-exec-view';

// Parse the declared/net weight out of a string-bound input or the redacted
// `data` projection (a Number, a numeric string, or absent) — the SSOT for the
// deviation predicate, shared by the container and the view.
export function asNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export interface PickupCompleteBodyArgs {
  clientOpId: string;
  releasingSig: string;
  agentSig: string;
  logisticsSig: string;
  agentEmployeeId: string;
  /** The string-bound net-weight input (parsed here). */
  netWeight: string;
  /** The order's declared weight (parsed by the container; null when absent). */
  declaredWeight: number | null;
  overrideReason: string;
  gpsState: GpsState;
}

/**
 * Build the {@link PickupCompleteBody} sent to `/driver/pickups/:id/complete`,
 * applying the geo-omit / reason-only invariants in one place (see file header).
 */
export function buildPickupCompleteBody(
  args: PickupCompleteBodyArgs
): PickupCompleteBody {
  const net = asNumber(args.netWeight);
  const deviates =
    net != null &&
    args.declaredWeight != null &&
    Math.abs(net - args.declaredWeight) > 0;

  const body: PickupCompleteBody = {
    client_op_id: args.clientOpId,
    releasing_signature_png_b64: args.releasingSig,
    agent_signature_png_b64: args.agentSig,
    logistics_signature_png_b64: args.logisticsSig,
    agent_employee_id: args.agentEmployeeId,
  };
  if (net != null) {
    body.net_weight_kg = net;
  }
  // Override reason ONLY when the weight deviates (the 4B.4 finding-sign-off
  // basis: an out-of-tolerance net weight must carry a justification).
  if (deviates && args.overrideReason.trim()) {
    body.net_weight_override_reason = args.overrideReason.trim();
  }
  // `geo` ONLY when coords were obtained — omitted on 'pending'/'denied'.
  if (args.gpsState !== 'pending' && args.gpsState !== 'denied') {
    body.geo = args.gpsState;
  }
  return body;
}
